import './select-second-half.css';

import EventEmitter from "eventemitter3";
import { Modal } from '../../../../../modal';
import { EventTypes } from "../../../../../../../core/event-bus";
import { HalfModifierSelection, Modals } from "../../../../../../../types/enums";
import { Context } from "../../../../../../../models/context";
import { DataLayerService } from "../../../../../../../services/data-layer-service";
import { Data } from "../../../../../../../models/data";
import { LocalizationService } from "../../../../../../../services/localization-service";
import { IDataItem, IItemModifierDisplayGroup } from "../../../../../../../models/data.interface";
import { MenuItemCard } from "../../../../../../shared/MenuItemCard/menu-item-card";
import { Order } from "../../../../../../../models/order";
import { IDealBuilderCustomizeItem } from '../../../../../DealBuilder/deal-builder.interface';

export interface ISelectSecondHalfProps {
    halfId: number,
    sizeId: number,
    item: IDataItem,
    mealDeal: IDealBuilderCustomizeItem | null
}

/**
 * A modal dialog for selecting the second half of a meal deal.
 */
export class SelectSecondHalf extends Modal {
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _itemElement: HTMLElement | null;
    private _state: ISelectSecondHalfProps;

    /**
     * Initializes a new instance of the SelectSecondHalfModal class.
     * @param eventBus The event bus instance.
     * @param data The data service instance.
     * @param order The order service instance.
     * @param context The context instance.
     * @param localizationService The localization service instance.
     * @param dataLayer The data layer service instance.
     */
    constructor(eventBus: EventEmitter, data: Data, order: Order, context: Context, localizationService: LocalizationService, dataLayer: DataLayerService) {
        super(
            "modal_select_second_half",
            eventBus,
            context,
            dataLayer,
            localizationService,
            {
                hasCloseButton: true,
                header: { title: "CHOOSE 2ND HALF" },
                ariaLabel: "Choose Second Half"
            },
            Modals.SELECT_SECOND_HALF
        );
        this._data = data;
        this._order = order;
        this._itemElement = document.getElementById("div_select_second_half_items") ?? null;
        this._state = {} as ISelectSecondHalfProps;

        this._eventBusListeners();
    }

    protected readonly _setEventListeners = (): void => { }

    /**
     * Subscribes to events on the event bus.
     * @private
     */
    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.SELECT_SECOND_HALF_OPEN, this._handleOpenModal);
    }

    /**
     * Handles the open modal event.
     * @private
     * @param {ISelectSecondHalfProps} props The modal properties.
     */
    private readonly _handleOpenModal = (props: ISelectSecondHalfProps): void => {
        this._state = props;
        this._renderHeader();
        this._renderContent();

        super.open();
    }

    private _renderHeader(): void {
        const title = this._state.halfId === HalfModifierSelection.LEFT ? "CHOOSE_1ST_HALF" : "CHOOSE_2ND_HALF";
        super.title = this._localizationService.names(title);
        super.init();
    }

    /**
     * Renders the modal content.
     * @private
     */
    private _renderContent(): void {
        if (!this._itemElement)
            return;

        this._itemElement.innerHTML = "";

        const items = this._getEligibleItems();

        for (const item of items) {
            const itemCard = new MenuItemCard(
                this._localizationService,
                item,
                () => { this._handleOnClick(item); }
            );
            this._itemElement.appendChild(itemCard.element);
        }
    }

    /**
     * Gets the eligible items for the meal deal.
     * @private
     * @returns {IDataItem[]} The eligible items.
     */
    private _getEligibleItems(): IDataItem[] {
        const orderTypeId = this._order.getProperty("OrderTypeID");
        const mealDeal = this._state.mealDeal;

        const nonHalfableDisplayGroupIds = this._data
            .getProperty("ModifierDisplayGroups")
            .filter(x => !x.IsHalfable)
            .map(x => x.ModifierDisplayGroupId);

        // Base candidate items: same category, halfable
        const candidateItems = this._data
            .getProperty("Items")
            .filter(item =>
                item.WebCategoryId === this._state.item.WebCategoryId &&
                item.IsHalfable
            );

        // Apply coupon + non-halfable compatibility filtering
        const couponEligibleItems = mealDeal?.coupon?.Items?.length
            ? candidateItems.filter(item => {
                const allowedSizes = this._order.getCouponItemSizesByGroup(
                    mealDeal.coupon,
                    item.ItemId,
                    mealDeal.selectedGroup
                );

                // must be allowed by coupon
                if (!allowedSizes.includes(this._state.sizeId)) {
                    return false;
                }

                // must have the same non-halfable display groups
                return this._hasSameNonHalfableDisplayGroups(
                    item,
                    nonHalfableDisplayGroupIds
                );
            })
            : candidateItems.filter(item =>
                this._hasSameNonHalfableDisplayGroups(
                    item,
                    nonHalfableDisplayGroupIds
                )
            );

        // Final eligibility: order type + size + halfable size
        return couponEligibleItems.filter(item => {
            const orderType = item.OrderTypes.find(x => x.OrderTypeId === orderTypeId);
            if (!orderType)
                return false;

            return orderType.Sizes.some(x => x.SizeId === this._state.sizeId && x.IsHalfable);
        });
    }

    /**
     * Determines whether the current item and the provided item contain
     * the same set of non-halfable modifier display groups.
     *
     * Only modifier display groups whose IDs are included in
     * `nonHalfableDisplayGroupIds` are considered.
     *
     * Order does not matter; comparison is based solely on the presence
     * of matching ModifierDisplayGroupIds.
     *
     * @param item - The item to compare against the current state item
     * @param nonHalfableDisplayGroupIds - List of modifier display group IDs
     *                                    that are considered non-halfable
     * @returns `true` if both items contain the exact same set of
     *          non-halfable modifier display groups; otherwise `false`
     */
    private _hasSameNonHalfableDisplayGroups(item: IDataItem, nonHalfableDisplayGroupIds: number[]): boolean {
        const getGroupIds = (groups: IItemModifierDisplayGroup[]) =>
            new Set(
                groups
                    .filter(g => nonHalfableDisplayGroupIds.includes(g.ModifierDisplayGroupId))
                    .map(g => g.ModifierDisplayGroupId)
            );

        const currentIds = getGroupIds(this._state.item.ModifierDisplayGroups);
        const itemIds = getGroupIds(item.ModifierDisplayGroups);

        if (currentIds.size !== itemIds.size)
            return false;

        for (const id of currentIds) {
            if (!itemIds.has(id))
                return false;
        }

        return true;
    }

    private _handleOnClick(menuItem: IDataItem): void {
        this._eventBus.emit(EventTypes.SELECT_SECOND_HALF_SELECTED, menuItem, this._state.halfId);
        this.close();
    }
}